import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppAccountPost - one POST whose answer is handed back RAW, as (status, text), exactly once.
//
// ⚠ WHY NOT `AppSignUpPost`: that reads every answer into `SignUpAnswer`, which has no field for the
// `/leave` step list - the rows would be dropped on the floor. The MEANING of the answer stays in a
// rules module (`AppAccount.ParseLeave`), tested without a browser and pinned against the service.
// ⚠ WHY NOT the shared REST transport: `AppSource.ts`'s header - it is SharePoint-bound.

export type AppRawDone = (Status: number, Text: string) => void;

/** Optional transport choices; existing callers retain their POST with no client deadline. */
export class AppRawOptions {
    public Method: string = "POST";
    public Headers: Record<string, string> = {};
    public TimeoutMs: number = 0;
    public Abort: AbortSignal | null = null;
}

export class AppAccountPost {
    private Url: string;
    private Body: string;
    private Done: AppRawDone;
    private Req: XMLHttpRequest | null = null;
    private Ended: boolean = false;
    private Options: AppRawOptions;

    public constructor(Url: string, Body: string, Done: AppRawDone, Options: AppRawOptions = new AppRawOptions()) {
        this.Url = Url;
        this.Body = Body;
        this.Done = Done;
        this.Options = Options;
    }

    public Send = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Ended === true) { return; }
            if (this.Options.Abort !== null && this.Options.Abort.aborted === true) { this.Abort(); return; }
            this.Req = new XMLHttpRequest();
            (this.Req as XMLHttpRequest).onreadystatechange = this.OnState;
            (this.Req as XMLHttpRequest).onerror = this.OnLost;
            (this.Req as XMLHttpRequest).ontimeout = this.OnLost;
            (this.Req as XMLHttpRequest).open(this.Options.Method, this.Url, true);
            (this.Req as XMLHttpRequest).timeout = Math.max(0, this.Options.TimeoutMs);
            if (this.Options.Method !== "GET") { (this.Req as XMLHttpRequest).setRequestHeader("Content-Type", "application/json"); }
            for (const Name of Object.keys(this.Options.Headers)) {
                (this.Req as XMLHttpRequest).setRequestHeader(Name, this.Options.Headers[Name]);
            }
            if (this.Options.Abort !== null) { this.Options.Abort.addEventListener("abort", this.Abort, { once: true }); }
            if (this.Options.Method === "GET") { (this.Req as XMLHttpRequest).send(); return; }
            (this.Req as XMLHttpRequest).send(this.Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.Send");
            // ⚠ STILL ANSWERS - as a lost connection, which the reader shows as "our end".
            this.Finish(0, "");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: departed views cancel transport even if another fault latched the gate. */
    public Abort = (): void => {
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.ReleaseSignal();
            if (this.Req === null) { return; }
            this.Req.onreadystatechange = null;
            this.Req.onerror = null;
            this.Req.ontimeout = null;
            this.Req.abort();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.Abort"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: timeout/network loss is an expected unknown outcome and must finish once. */
    private OnLost = (): void => {
        try { this.Finish(0, ""); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.OnLost"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: completion must remove its signal listener under a faulted gate too. */
    private ReleaseSignal = (): void => {
        try {
            if (this.Options.Abort !== null) { this.Options.Abort.removeEventListener("abort", this.Abort); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.ReleaseSignal"); }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the page is waiting on this answer. */
    private OnState = (): void => {
        try {
            const Req: XMLHttpRequest = this.Req as XMLHttpRequest;
            if (Req.readyState !== 4) { return; }
            this.Finish(Req.status, Req.responseText);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.OnState");
        }
    };

    /** ⛔ The ONLY place `Done` is called, latched before delivering. */
    private Finish = (Status: number, Text: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the one thing that releases the page.
        try {
            if (this.Ended === true) { return; }
            this.Ended = true;
            this.ReleaseSignal();
            this.Done(Status, Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAccountPost.Finish");
        }
    };
}
